import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ABTestingLab } from '@/components/ABTestingLab';
import { useABTestingSessions } from '@/hooks/useABTestingSessions';
import { FlaskConical, Plus, Trophy, Clock, Archive, Loader2, BarChart, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const ABTesting = () => {
  const navigate = useNavigate();
  const [showLab, setShowLab] = useState(false);
  const [viewingSessionId, setViewingSessionId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [testToDelete, setTestToDelete] = useState<string | null>(null);
  const { sessions, loading, deleteSession } = useABTestingSessions();

  const activeTests = sessions.filter(s => s.status === 'active');
  const completedTests = sessions.filter(s => s.status === 'completed');

  const handleDeleteClick = (testId: string, testName: string) => {
    setTestToDelete(testId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!testToDelete) return;
    
    try {
      await deleteSession(testToDelete);
      toast.success('Test deleted successfully');
      setDeleteDialogOpen(false);
      setTestToDelete(null);
    } catch (error) {
      toast.error('Failed to delete test');
      console.error('Delete error:', error);
    }
  };

  if (showLab) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Header />
        <main className="container mx-auto px-4 py-8 pt-24">
          <ABTestingLab existingSessionId={viewingSessionId} />
          <div className="mt-6 flex justify-center">
            <Button
              variant="secondary"
              onClick={() => {
                setShowLab(false);
                setViewingSessionId(null);
              }}
            >
              Back to Test List
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Header />
      <main className="container mx-auto px-4 py-8 pt-24">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <FlaskConical className="w-16 h-16 text-blue-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            A/B Testing Lab
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
            Compare different creative directions systematically and make data-driven decisions
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              size="lg"
              variant="default"
              onClick={() => setShowLab(true)}
            >
              <Plus className="w-5 h-5 mr-2" />
              Create New Test
            </Button>
            <Button
              size="lg"
              variant="default"
              onClick={() => navigate('/ab-testing/analytics')}
            >
              <BarChart className="w-5 h-5 mr-2" />
              View Analytics
            </Button>
          </div>
        </div>

        {/* Active Tests */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading tests...</p>
          </div>
        ) : (
          <>
            {activeTests.length > 0 && (
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                  <Clock className="w-6 h-6 mr-2 text-blue-400" />
                  Active Tests ({activeTests.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {activeTests.map((test) => (
                    <Card key={test.id} className="bg-white/5 border-white/10 p-6 hover:border-blue-500/50 transition-all">
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-white font-semibold text-lg mb-2">{test.name}</h3>
                          {test.description && (
                            <p className="text-gray-400 text-sm">{test.description}</p>
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          <Badge variant="secondary">
                            {test.test_type.replace('_', ' ')}
                          </Badge>
                          <span className="text-xs text-gray-400">
                            {formatDistanceToNow(new Date(test.created_at), { addSuffix: true })}
                          </span>
                        </div>

                        <div className="text-sm text-gray-400">
                          Total Votes: {test.total_votes}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="default"
                            className="flex-1"
                            onClick={() => {
                              setViewingSessionId(test.id);
                              setShowLab(true);
                            }}
                          >
                            View Test
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteClick(test.id, test.name)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {/* Completed Tests */}
            {completedTests.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                  <Trophy className="w-6 h-6 mr-2 text-yellow-400" />
                  Completed Tests ({completedTests.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {completedTests.map((test) => (
                    <Card key={test.id} className="bg-white/5 border-white/10 p-6 hover:border-yellow-500/50 transition-all">
                      <div className="space-y-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-white font-semibold text-lg mb-2">{test.name}</h3>
                            {test.description && (
                              <p className="text-gray-400 text-sm">{test.description}</p>
                            )}
                          </div>
                          <Trophy className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                        </div>

                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="border-yellow-500 text-yellow-400">
                            Completed
                          </Badge>
                          <span className="text-xs text-gray-400">
                            {test.completed_at && formatDistanceToNow(new Date(test.completed_at), { addSuffix: true })}
                          </span>
                        </div>

                        <div className="text-sm text-gray-400">
                          Total Votes: {test.total_votes}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => {
                              setViewingSessionId(test.id);
                              setShowLab(true);
                            }}
                          >
                            View Results
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteClick(test.id, test.name)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {/* Empty State */}
            {sessions.length === 0 && !loading && (
              <div className="text-center py-16">
                <FlaskConical className="w-24 h-24 text-gray-600 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-white mb-4">No A/B Tests Yet</h3>
                <p className="text-gray-400 mb-8 max-w-md mx-auto">
                  Create your first A/B test to compare different creative directions and find what works best
                </p>
                <Button
                  size="lg"
                  onClick={() => setShowLab(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create Your First Test
                </Button>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-slate-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Test?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will permanently delete this A/B test and all its variants. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ABTesting;
